import { useEffect, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
    createFilterOptions,
    FilterOptionsState,
} from '@mui/material/useAutocomplete';
import { useTranslation } from 'react-i18next';
import { mdiClose, mdiPlus, mdiSync } from '@mdi/js';
import { FieldValues, useController } from 'react-hook-form';

import {
    LocationsDocument,
    ILocationFragment,
    ILocationsQueryVariables,
    ILocationStatus,
} from 'graphql/types';
import { useApolloError } from 'common/hooks/useApolloError';
import { getLocationStatusString } from 'common/utils/location';
import { Chip } from 'common/components/Chip';
import { IconButton } from 'common/components/IconButton';
import { Icon } from 'common/components/Icon';
import { ActionButton } from 'common/components/ActionButton';
import { TControllerProps } from 'common/types';
import { asTypedControl } from 'common/utils/form';
import { AutocompleteField } from 'common/forms/Field/AutocompleteField';
import { TextField } from 'common/forms/Field';

interface ILocationOption extends Partial<ILocationFragment> {
    inputValue?: string;
}

const filter = createFilterOptions<ILocationOption>();

type TAvailabilityFilters = Pick<
    ILocationsQueryVariables,
    'availabilityStartDate' | 'availabilityEndDate'
>;

interface IProps {
    allowCreateNew?: boolean;
    allowCreateString?: boolean;
    label?: string;
    disableClearable?: boolean;
    allowRefetch?: boolean;
    availabilityFilters?: TAvailabilityFilters;
    name: string;
    showNewLocationFields?: boolean;
    onClickNewLocation?(showNewLocationFields: boolean): void;
}

export const LocationSelectField = <TFieldValues extends FieldValues>({
    control: formControl,
    name,
    label,
    availabilityFilters,
    disableClearable,
    allowRefetch,
    showNewLocationFields,
    allowCreateNew,
    allowCreateString,
    onClickNewLocation,
    ...other
}: IProps & TControllerProps<TFieldValues>) => {
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();

    const prevFilters = useRef<TAvailabilityFilters>(availabilityFilters);
    const [filtersChanged, setFiltersChanged] = useState(false);

    const [fetchLocations, { data, loading, error }] = useLazyQuery(
        LocationsDocument,
        { fetchPolicy: 'no-cache' }
    );

    useEffect(() => {
        if (error) {
            showApolloError(error);
        }
    }, [error, showApolloError]);

    useEffect(() => {
        if (data) {
            setFiltersChanged(false);
        }
    }, [data]);

    const locations = (data?.locations || []).filter(
        (location): location is ILocationFragment =>
            location !== undefined && location.id !== undefined
    );

    const control = asTypedControl<ILocationOption>(name, formControl);

    const { field } = useController({
        name,
        control,
    });

    // Hide new fields when a location is selected
    useEffect(() => {
        if (field.value && showNewLocationFields) {
            onClickNewLocation?.(false);
        }
    }, [field.value]);

    const locationOptions: ILocationOption[] = locations.map((location) => ({
        id: location.id,
        title: location.title,
        location: location.location,
        status: location.status,
        description: location.description,
    }));

    useEffect(() => {
        if (!availabilityFilters) return;

        if (
            JSON.stringify(availabilityFilters) ===
            JSON.stringify(prevFilters.current)
        ) {
            return;
        }

        setFiltersChanged(true);
        prevFilters.current = availabilityFilters;
    }, [availabilityFilters]);

    // Get status of selected location
    const selectedLocationStatus = locationOptions.find(
        (option) => field.value?.id === option.id
    )?.status;

    const handleChange = (
        _event: React.SyntheticEvent,
        value: ILocationOption | string | null
    ) => {
        if (typeof value === 'string' || !value) {
            field.onChange(value);

            return;
        }

        // If inputValue is set we want to use that as title, this happens when a value is created via the
        // filterOptions function, because title is a translated value
        const newValue = value.inputValue
            ? { ...value, title: value.inputValue }
            : value;

        field.onChange(newValue);
    };

    const handleFilterOptions = (
        options: ILocationOption[],
        params: FilterOptionsState<ILocationOption>
    ) => {
        const filtered = filter(options, params);

        const { inputValue } = params;

        // Suggest the creation of a new value
        const isExisting = options.some(
            (option) => inputValue === option.title
        );

        if (!!inputValue && !isExisting) {
            filtered.push({
                inputValue: inputValue,
                title: `${translate('useLocationInputValue')} "${inputValue}"`,
            });
        }

        return filtered;
    };

    let onChange:
        | ((
              event: React.SyntheticEvent,
              value: ILocationOption | string | null
          ) => void)
        | undefined;
    let onFilterOptions:
        | ((
              options: ILocationOption[],
              params: FilterOptionsState<ILocationOption>
          ) => ILocationOption[])
        | undefined;

    if (allowCreateString && !allowCreateNew) {
        onChange = handleChange;
        onFilterOptions = handleFilterOptions;
    }

    return (
        <>
            <Box sx={{ display: 'flex' }}>
                <AutocompleteField
                    {...field}
                    clearOnBlur
                    selectOnFocus
                    defaultValue=""
                    disableClearable={disableClearable}
                    endAdornment={
                        error || (allowRefetch && filtersChanged) ? (
                            <IconButton
                                iconPath={mdiSync}
                                iconSize="2.2rem"
                                onClick={() => {
                                    fetchLocations({
                                        variables: availabilityFilters,
                                    });
                                }}
                            />
                        ) : (
                            selectedLocationStatus &&
                            selectedLocationStatus !==
                                ILocationStatus.Unknown && (
                                <Chip
                                    label={getLocationStatusString(
                                        selectedLocationStatus
                                    )}
                                />
                            )
                        )
                    }
                    filterOptions={
                        !!onFilterOptions
                            ? (options, params) =>
                                  onFilterOptions(options, params)
                            : undefined
                    }
                    freeSolo={!!allowCreateString && !allowCreateNew}
                    getOptionLabel={(option) => {
                        // Value selected with enter, right from the input
                        if (typeof option === 'string') {
                            return option;
                        }

                        if (option.inputValue) {
                            return option.inputValue;
                        }

                        return option.title ?? '';
                    }}
                    InputProps={{ label }}
                    isOptionEqualToValue={(option, value) => {
                        if (option.id) {
                            return option.id === value.id;
                        }

                        return option.title === value.title;
                    }}
                    loading={loading}
                    name={name}
                    options={locationOptions}
                    renderOption={(props, option) => (
                        <Box
                            component="li"
                            {...props}
                            key={option.id || option.title}
                        >
                            <Box>
                                <Box>
                                    <Typography
                                        sx={{ fontWeight: 700 }}
                                        variant="body1"
                                    >
                                        {option.title}
                                    </Typography>
                                </Box>
                                {option.location && (
                                    <Box>
                                        <Typography variant="body2">
                                            {option.location}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                            {!!option.status &&
                                option.status !== ILocationStatus.Unknown && (
                                    <Box sx={{ ml: 'auto' }}>
                                        <Chip
                                            label={getLocationStatusString(
                                                option.status
                                            )}
                                        />
                                    </Box>
                                )}
                        </Box>
                    )}
                    sx={[
                        { width: '100%' },
                        allowCreateNew ? { mr: 2 } : { mr: 0 },
                    ]}
                    onChange={onChange}
                    onOpen={() => {
                        // Only refetch locations when filters are changed
                        if (
                            !error &&
                            data &&
                            (!filtersChanged || !allowRefetch)
                        ) {
                            return;
                        }

                        fetchLocations({
                            variables: availabilityFilters,
                        });
                    }}
                    {...other}
                />

                {allowCreateNew && !showNewLocationFields && (
                    <ActionButton
                        outlined
                        aria-label={translate('moduleForm.meeting.newTitle')}
                        sx={{ ml: 'auto', flexShrink: 0, mt: '21px' }}
                        onClick={() => {
                            onClickNewLocation?.(true);

                            // Clear field value if new button is clicked
                            if (field.value) field.onChange('');
                        }}
                    >
                        <Icon path={mdiPlus} />
                    </ActionButton>
                )}

                {allowCreateNew && showNewLocationFields && (
                    <IconButton
                        aria-label={translate(
                            'moduleForm.meeting.locationDelete'
                        )}
                        iconPath={mdiClose}
                        iconSize="2.4rem"
                        sx={{ height: 40, mt: '24px' }}
                        onClick={() => {
                            onClickNewLocation?.(false);
                        }}
                    />
                )}
            </Box>

            {allowCreateNew && showNewLocationFields && (
                <>
                    <TextField
                        control={control}
                        label={translate('title')}
                        name="newLocation.title"
                    />

                    <TextField
                        control={control}
                        label={translate('address')}
                        name="newLocation.location"
                    />

                    <TextField
                        multiline
                        control={control}
                        label={translate('description')}
                        name="newLocation.description"
                    />
                </>
            )}
        </>
    );
};
